import {Dropdown, DropdownItem, Search} from 'platform/components';
import {Box, Show} from 'platform/foundation';

import {startTransition, useEffect, useRef, useState} from 'react';

import {isNotNilOrEmpty} from 'ramda-adjunct';

import {TestIdProps, suffixTestId} from 'shared/web';

import {SMART_SEARCH_KEY} from '../../../../constants/smartSearchKey';
import {useGridKeyboardNav} from '../../../../context/GridKeyboardNavContext';
import {useSmartSearchFilter} from '../../../../store/selectors/useSmartSearchFilter';
import {useEagGridStore} from '../../../../store/useEagGridStore';
import {useSmartSearchHistory} from './hooks/useSmartSearchHistory';

const ENTER_KEY = 'Enter';
const ESCAPE_KEY = 'Escape';

type QuickFilterInputProps = {
  initialValue?: string;
  onSubmit?: () => void;
  onEscape?: () => void;
  className?: string;
  autoFocus?: boolean;
} & TestIdProps;

export function QuickFilterInput(props: QuickFilterInputProps) {
  // Fine-grained selector — only re-renders when the smart search value changes,
  // not on every filter change (column filters, etc.).
  const smartSearchValue = useSmartSearchFilter();
  // Stable store reference for reading latest filterValues in event handlers (no subscription).
  const store = useEagGridStore();
  const history = useSmartSearchHistory();
  const nav = useGridKeyboardNav();

  const containerRef = useRef<HTMLDivElement>(null);

  const [localValue, setLocalValue] = useState<string>(props.initialValue ?? smartSearchValue);
  const [prevSmartSearch, setPrevSmartSearch] = useState(smartSearchValue);

  // eslint-disable-next-line eslint-js/no-restricted-syntax -- intentional: one-time DOM focus with rAF cleanup on unmount
  useEffect(function focusInputOnMount() {
    if (!props.autoFocus) {
      return;
    }
    // Delay to run after the modal's focus trap has settled
    const rafId = requestAnimationFrame(() => {
      containerRef.current?.querySelector<HTMLInputElement>('input')?.focus();
    });
    return () => cancelAnimationFrame(rafId);
  }, []); // eslint-disable-line react/exhaustive-deps -- Muted pre-existing violation

  /**
   * In-render state adjustment: sync external store value to local input state.
   * This replaces a useEffect + setState pattern. React handles setState during
   * render as a synchronous bail-out — the component re-renders immediately with
   * the new value before committing to the DOM, avoiding the double-render cycle
   * that the useEffect approach causes.
   *
   * @see https://react.dev/learn/you-might-not-need-an-effect#adjusting-some-state-when-a-prop-changes
   */
  if (prevSmartSearch !== smartSearchValue) {
    setPrevSmartSearch(smartSearchValue);
    setLocalValue(smartSearchValue);
  }

  const submitSearch = (value: string) => {
    const currentFilters = store.getState().filterValues;
    const nextFilters = {...currentFilters};

    if (value) {
      nextFilters[SMART_SEARCH_KEY] = {[SMART_SEARCH_KEY]: value};
    } else {
      delete nextFilters[SMART_SEARCH_KEY];
    }

    // Non-urgent: wrap in startTransition so the input stays responsive during
    // the grid re-render triggered by the filter change (rerender-transitions).
    startTransition(() => {
      store.setFilterValues(nextFilters);
    });

    history.refetch();
  };

  const handleKeyDown = (event: React.KeyboardEvent) => {
    if (event.key === ENTER_KEY) {
      event.preventDefault();
      history.closeMenu();
      submitSearch(localValue);
      props.onSubmit?.();
    } else if (event.key === ESCAPE_KEY) {
      history.closeMenu();
      props.onEscape?.();
    }
  };

  const handleChange = (value: string | null) => {
    setLocalValue(value ?? '');
    // An empty value means the field is being emptied, not typed into — Search's
    // clear button reports the clear as onChange(null). Opening the history menu on
    // that left it hanging over the grid's first rows, where it swallowed the user's
    // next row click (T20-139432), even though the tabIndex={-1} clear button never
    // focused the field. Suppressing the open here rather than closing the menu again
    // in handleClear keeps the fix independent of the order Search calls onChange and
    // onClear in. Emptying the field by keyboard needs no open either: the menu is
    // already open from handleFocus.
    if (isNotNilOrEmpty(value) && !history.menuOpen) {
      history.openMenu();
    }
  };

  const handleClear = () => {
    setLocalValue('');
    // Covers the other half of T20-139432: when the field WAS focused the menu is
    // already open, so handleChange's guard cannot help and handleBlur deliberately
    // keeps the menu alive while focus stays in the container (the clear button is
    // inside it). Without this, clearing a focused field leaves the menu over the grid.
    history.closeMenu();
    submitSearch('');
  };

  const handleFocus = (event: React.FocusEvent<HTMLInputElement>) => {
    event.target.select();
    history.openMenu();
  };

  const handleBlur = (event: React.FocusEvent<HTMLDivElement>) => {
    const nextFocused = event.relatedTarget as HTMLElement | null;
    // Keep the menu open while focus stays inside the search field (e.g. the clear button)...
    if (nextFocused && event.currentTarget.contains(nextFocused)) {
      return;
    }
    // ...or while focus is inside the history menu. Hovering an option focuses it
    // (floating-ui useListNavigation focusItemOnHover), and when the pointer leaves
    // an option floating-ui parks focus on the menu element itself. Both live in a
    // portal outside this container, so the contains() check above can't see them —
    // without this guard the menu would dismiss mid-hover (T20-138881), and a
    // clicked option's onClick would never fire.
    if (nextFocused?.closest('[role="menu"], [role="menuitem"]')) {
      return;
    }
    // Otherwise focus has left the search field (e.g. Tab into the grid) — close the history menu.
    history.closeMenu();
  };

  const handleOptionClick = (value: string) => {
    setLocalValue(value);
    history.closeMenu();
    submitSearch(value);
  };

  return (
    <div
      ref={(el) => {
        containerRef.current = el;
        nav?.registerSearchContainer(el);
      }}
      onKeyDown={handleKeyDown}
      onBlur={handleBlur}
      onContextMenu={(event) => event.stopPropagation()}
      className={props.className ?? 'w-full min-w-0'}
    >
      <Box position="relative">
        <div className="[&>div>div]:!border-[#C1C7D0] [&>div>div]:!bg-white [&>div>div:focus-within]:!border-[#4C9AFF]">
          <Search
            value={localValue}
            onChange={handleChange}
            onClear={handleClear}
            onFocus={handleFocus}
            data-testid={suffixTestId('quickFilter', props)}
          />
        </div>
        <Show when={history.searchOptions?.length}>
          {/* The Dropdown's reference is an inline span around the control; with a
              block-level control inside, browsers disagree where its collapsed 0x0
              rect sits (Chromium: block's bottom, Firefox: block's top — T20-138624).
              A zero-height control anchored at the input's bottom edge makes
              top == bottom, so every engine measures the same point. */}
          <Box position="absolute" bottom={0} left={0}>
            <Dropdown
              dropdownControl={<div />}
              isOpen={history.menuOpen}
              onClose={history.closeMenu}
              strategy="fixed"
              isFocusManagementDisabled
              data-testid={suffixTestId('quickFilterDropdown', props)}
            >
              {history.searchOptions?.map((option) => (
                <DropdownItem
                  key={option.value}
                  label={option.label}
                  onClick={() => handleOptionClick(option.value)}
                />
              ))}
            </Dropdown>
          </Box>
        </Show>
      </Box>
    </div>
  );
}
